import { useEmailSenderIdentity } from "@/features/material-compliance/api/inquiryEmailApi"
import { useTemplateSettings } from "@/features/material-compliance/api/templateSettingsApi"
import { useOrganization } from "@/services/api/organizationApi"
import { useOrgSignatures } from "@/services/api/organizationSignaturesApi"
import type { ContentLanguage } from "../components/SettingsControls"
import type { SettingsSectionId } from "../paths"
import {
  settingsTarget,
  settingsTargetPath,
  type SettingsTargetId,
} from "../settingsTargets"
import type { SectionVisibility } from "../useSettingsVisibility"
import {
  companyDetailsNeedSetup,
  emailSignatureLanguagesNeedingSetup,
  emailSignatureNeedsSetup,
  logoNeedsSetup,
  pdfFooterNeedsSetup,
  pdfFooterPrints,
  sendingAddressNeedsSetup,
  signatureImageNeedsSetup,
} from "./setupChecks"

export type SetupItemId =
  | "companyDetails"
  | "logo"
  | "sendingAddress"
  | "emailSignature"
  | "pdfFooter"
  | "signatureImage"

/** `loading`/`error`: the data the check reads hasn't arrived (yet). */
export type SetupItemState = "done" | "todo" | "loading" | "error"

export interface SetupItem {
  id: SetupItemId
  /** The settings page the item is set on. */
  section: SettingsSectionId
  /** Where to set it: its section on that page (or tab). */
  path: string
  state: SetupItemState
  /** For a text written in each language: which ones still need writing. */
  languages?: Record<ContentLanguage, boolean>
}

export interface SettingsSetup {
  items: SetupItem[]
  /** How many items are still to do on each page, for the nav. */
  todoBySection: Partial<Record<SettingsSectionId, number>>
  /**
   * Whether anything is left to set up: `some` once a check found something
   * (or failed, so nothing is confirmed), `none` once every check passed,
   * `unknown` while checks are still loading and none has found anything.
   */
  remaining: "some" | "none" | "unknown"
  retry: () => void
}

interface QueryLike<T> {
  data: T | undefined
  isError: boolean
}

/** Where an item is set: its page, and the link to its section there, which
 *  then leads back to the checklist. */
function placeOf(target: SettingsTargetId) {
  return {
    section: settingsTarget(target).section,
    path: settingsTargetPath(target, { fromSetup: true }),
  }
}

/** The item's state from its query: what `needsSetup` says once loaded. */
function stateOf<T>(
  query: QueryLike<T>,
  needsSetup: (data: T) => boolean,
): SetupItemState {
  if (query.data !== undefined) return needsSetup(query.data) ? "todo" : "done"
  return query.isError ? "error" : "loading"
}

/**
 * What this user's org still has to set up before its documents and emails
 * go out right: one item per setting that ships empty or with a placeholder.
 * Only items on pages the user gets, so every item links somewhere they can
 * open, and no query runs for a page they can't.
 */
export function useSettingsSetup(
  visibility: Record<SettingsSectionId, SectionVisibility>,
): SettingsSetup {
  const visible = (id: SettingsSectionId) => visibility[id] === "visible"
  const inquiries = visible("emailSending") || visible("supplierInquiries")

  const org = useOrganization()
  const settings = useTemplateSettings(inquiries)
  const identity = useEmailSenderIdentity(visible("emailSending"))
  const signatures = useOrgSignatures(visible("signatures"))

  const items: SetupItem[] = []
  if (visible("general")) {
    items.push(
      {
        id: "companyDetails",
        ...placeOf("org.details"),
        state: stateOf(org, companyDetailsNeedSetup),
      },
      {
        id: "logo",
        ...placeOf("org.logo"),
        state: stateOf(org, logoNeedsSetup),
      },
    )
  }
  if (visible("emailSending")) {
    items.push(
      {
        id: "sendingAddress",
        ...placeOf("email.sendingAddress"),
        state: stateOf(identity, sendingAddressNeedsSetup),
      },
      {
        id: "emailSignature",
        ...placeOf("email.signature"),
        state: stateOf(settings, emailSignatureNeedsSetup),
        languages: settings.data
          ? emailSignatureLanguagesNeedingSetup(settings.data)
          : undefined,
      },
    )
  }
  // Left out once loaded if the org's format never prints the footer.
  if (
    visible("supplierInquiries") &&
    (!settings.data || pdfFooterPrints(settings.data))
  ) {
    items.push({
      id: "pdfFooter",
      ...placeOf("inquiry.footer"),
      state: stateOf(settings, pdfFooterNeedsSetup),
    })
  }
  if (visible("signatures")) {
    items.push({
      id: "signatureImage",
      ...placeOf("signatures"),
      state: stateOf(signatures, signatureImageNeedsSetup),
    })
  }

  const todoBySection: SettingsSetup["todoBySection"] = {}
  for (const item of items) {
    if (item.state === "todo")
      todoBySection[item.section] = (todoBySection[item.section] ?? 0) + 1
  }

  const remaining: SettingsSetup["remaining"] = items.some(
    (item) => item.state === "todo" || item.state === "error",
  )
    ? "some"
    : items.some((item) => item.state === "loading")
      ? "unknown"
      : "none"

  function retry() {
    for (const query of [org, settings, identity, signatures])
      if (query.isError) void query.refetch()
  }

  return { items, todoBySection, remaining, retry }
}
